<script setup lang="ts">
import { useMate } from "@/stores/mate";
import { GALLERY_MODULE_ID } from "@/stores/module/moduleMate";
import { Mate } from "@/types/entity";
import { useName } from "@/use/common";
import { showConfirmDialog, showLoadingToast } from "vant";
import { useI18n } from "vue-i18n";
import { RouteLocationRaw, useRouter } from "vue-router";

const mateStore = useMate();
const router = useRouter();
const { t } = useI18n();

const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
}>();

const onChangeMate = async (mateId: number) => {
  const toast = showLoadingToast({
    message: t("Message.loadingCharacter"),
    duration: 0,
  });
  try {
    await mateStore.changeCurrentMate(mateId);
  } finally {
    toast.close();
  }
};

const onMateItemClicked = async (mate: Mate, to: RouteLocationRaw) => {
  emit("update:show", false);
  if (mateStore.currentMate?.id !== mate.id) await onChangeMate(mate.id);
  // 2. 当前路由允许Mate切换：直接切换
  router.push(to);
};

const clickDelete = async (mate: Mate) => {
  try {
    await showConfirmDialog({
      title: t("Message.deletePresetCharacter"),
      message: t("Message.deletePresetCharacterConfirm"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
  } catch (error) {
    return;
  }
  const loading = showLoadingToast({
    message: t("Message.deletingCharacter"),
    duration: 0,
  });
  try {
    await mateStore.deleteCurrentMate(mate);
  } finally {
    loading.close();
  }
};
</script>
<template>
  <div class="px-5 overflow-hidden w-full">
    <div class="w-full flex flex-col gap-2 py-2">
      <div v-if="mateStore.fullList.length" class="grid grid-cols-1 sm:gap-4">
        <div
          v-for="item in mateStore.list"
          :key="item.id"
          class="h-24 overflow-hidden cursor-pointer rounded-sm flex flex-shrink-0 relative gap-2 py-2 border-b border-b-base-200 sm:border-b-0"
        >
          <!-- Avatar -->
          <div
            class="relative"
            @click="
              onMateItemClicked(item, {
                name: 'mateEdit',
                params: {
                  uuid: item.uuid,
                },
              })
            "
          >
            <span
              class="absolute right-1 bottom-1 z-10 badge badge-sm"
              v-if="item.isPreset"
            >
              {{ $t("User.mate.preset") }}
            </span>
            <span
              class="absolute right-1 bottom-1 z-10 badge badge-sm"
              v-else-if="item.status === 0"
            >
              {{ $t("User.mate.draft") }}
            </span>
            <UserAvatar :avatar-url="item.mediaUrl" class="h-full" />
          </div>
          <!-- Info -->
          <div class="flex flex-col justify-between flex-1">
            <div class="flex flex-col text-xs gap-0.5">
              <div class="flex gap-1 justify-between">
                <!-- <div class="text-base-content/50">
                  {{ $t("User.mate.basicInfo.name") }}:
                </div> -->
                <div
                  class="line-clamp-1 text-base-content text-sm font-semibold"
                >
                  {{ useName(item.name) }}
                </div>

                <button
                  v-if="item.isPreset"
                  class="btn btn-xs btn-outline"
                  @click="clickDelete(item)"
                >
                  <i class="iconify mingcute--delete-2-line"></i>
                </button>
              </div>
              <div class="text-base-content/50 line-clamp-1">
                {{ $t("User.mate.relation.myRelation") }}:
                <span v-html="item.relationWithMe || $t(`Cell.notSet`)"></span>
              </div>
              <div class="text-base-content/50 line-clamp-1">
                {{ $t("User.mate.basicInfo.bornPlanet") }}:
                {{ item.worldview?.name || $t(`Message.default`) }}
              </div>
              <!-- <div class="flex gap-1">
                <div class="text-base-content/50">
                  {{ $t("Cell.lastUpdatedTime") }}:
                </div>
                <div class="line-clamp-1 text-base-content/50">
                  {{ timeAgo(item.dtUpdate) }}
                </div>
              </div> -->
            </div>
            <div class="flex justify-end gap-2">
              <button
                class="btn btn-xs rounded gap-1"
                @click="
                  onMateItemClicked(item, {
                    name: 'mateEdit',
                    params: { uuid: item.uuid },
                    hash: '#' + GALLERY_MODULE_ID.toString(),
                  })
                "
              >
                <i class="mdi--folder-media-outline iconify text-sm" />
                <span>
                  {{ $t("User.mate.module.gallery") }}
                </span>
              </button>
              <button
                class="btn btn-xs rounded gap-1"
                @click="
                  onMateItemClicked(item, {
                    name: 'mateEdit',
                    params: { uuid: item.uuid },
                  })
                "
              >
                <i class="mingcute--edit-2-line iconify text-sm" />
                <span>
                  {{ $t("Buttons.setting") }}
                </span>
              </button>
              <RouterLink
                :to="{ name: 'mateHome', params: { uuid: item.uuid } }"
              >
                <button
                  class="btn btn-xs btn-neutral rounded gap-1"
                  @click="emit('update:show', false)"
                >
                  <i class="mingcute--user-2-line iconify"></i>
                  <span>
                    {{ $t("Buttons.viewCharacterCard") }}
                  </span>
                </button>
              </RouterLink>
            </div>
          </div>
        </div>
      </div>
      <template v-else>
        <div
          v-for="(rang, idx) in Array(1)"
          :key="idx"
          class="skeleton-loading-bg w-[90px] aspect-[90/140] rounded-lg shrink-0"
        />
      </template>
    </div>
  </div>
</template>
